import clsx from "clsx";
import { FC, useEffect } from "react";
import { twMerge } from "tailwind-merge";

import { switchOrg } from "api/api-server-actions";
import { OrgType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { ClickEvent, Menu, MenuItem } from "mds/components/Menu";
import { ChevronDownIcon, HomeHouseIcon } from "mds/icons";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import { selectOrgs } from "store/selectors";
import { aiStateActions } from "store/slices/ai";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import { GENERAL_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { BASE_SECTION_URL } from "utils/urls";

/**
 * A dropdown list of orgs.
 */
export type OrgSelectProps = {
  className?: string;
};

export const OrgSelect: FC<OrgSelectProps> = ({ className }) => {
  const orgs = useAppSelector(selectOrgs);
  const dispatch = useAppDispatch();

  const currentOrg = orgs.find((org) => org.current === true);
  const hasChoices = orgs.length > 1;

  useEffect(() => {
    storeApi.orgs.list();
  }, []);

  if (orgs.length <= 0 || !currentOrg) {
    return null;
  }

  const MenuButton = (
    <Button
      className={twMerge("gap-2 max-md:max-w-48", hasChoices && "font-medium", className)}
      disabled={!hasChoices}
      eventName="Switch Institution"
      kind={hasChoices ? "gray" : "grayInert"}
      size="s"
    >
      <HomeHouseIcon className="shrink-0" />
      <span className="truncate">{currentOrg?.name}</span>
      {hasChoices && <ChevronDownIcon className="shrink-0" />}
    </Button>
  );

  const onSelectOrg = (org: OrgType) => {
    switchOrg(org.id)
      .then((resp) => {
        trackEvent("New Institution selected", {
          ...GENERAL_EVENT_CATEGORY,
          newOrgName: resp.data.name,
        });
        dispatch(aiStateActions.clearAiChatMessages());

        // Redirect to the same page only if it's not a course page
        // which would be specific to the org we were just on.
        const newUrl = window.location.pathname.includes(BASE_SECTION_URL)
          ? window.location.origin
          : window.location.origin + window.location.pathname;

        window.location.href = newUrl;
      })
      .catch(toastLocalizedOperationCatcher("switching_org"));
  };

  if (!hasChoices) {
    return MenuButton;
  }

  return (
    <Menu align="start" menuButton={MenuButton} title={t("home.header.org_select")}>
      {orgs.map((org) => (
        <OrgMenuItem
          currentOrgId={currentOrg.id}
          key={org.id}
          org={org}
          onClick={() => onSelectOrg(org)}
        />
      ))}
    </Menu>
  );
};

type OrgMenuItemProps = {
  org: OrgType;
  currentOrgId: string;
  onClick: (event: ClickEvent) => void;
};

const OrgMenuItem: FC<OrgMenuItemProps> = ({ org, currentOrgId, onClick }) => {
  const checked = org.id === currentOrgId;

  return (
    <MenuItem
      checked={checked}
      className={clsx("gap-2", { selected: checked })}
      value={org}
      onClick={onClick}
    >
      {org?.name}
    </MenuItem>
  );
};
